import { useLanguage } from '../providers/preferenceProvider';
import { Link, useSearchParams } from 'react-router-dom';
import '../css/bookings.css';
import BookingsList from '../shared/booking/bookingsList';
import { useCallback, useEffect, useState } from 'react';
import { getUserBookings } from '../api/booking-api';
import { getRooms } from '../api/room-api';
import Overlay from '../shared/overlays/editBooking';
import { useConfig } from '../providers/configProvider';
import { getFiltersBookingsPage } from '../api/filter-api';
import FiltersList from '../shared/filters/filtersList';
import SearchComponent from '../shared/searchComponent';
import { ACL } from '../acls';

const Bookings = () => {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState({});
  const [showOverlay, setShowOverlay] = useState(false);
  const { config, isAuthorized } = useConfig();

  // Filters
  const [filtersList, setFiltersList] = useState([]);
  const [clear, setClear] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination
  const [hasMore, setHasMore] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const getFilters = async () => {
      setFiltersList((await getFiltersBookingsPage()).data);
    };
    
    getFilters();
    searchParams.set('pageSize', 3);
    setSearchParams(searchParams);
  }, []);

  useEffect(() => { 
    const timeout = setTimeout(() => {
      fetchData(true);
    }, 100);
    
    return () => clearTimeout(timeout); // return cleanup
  }, [clear, searchParams]);

  function openOverlay(booking) {
    booking.startTime = new Date(booking.startTime);
    booking.endTime = new Date(booking.endTime);
    setSelectedBooking(booking);
    setShowOverlay(true);
  }

  function closeOverlay() {
    setShowOverlay(false);
    fetchData(true);
  }

  const fetchData = useCallback( async (refresh = false) => {
    const queryParams = new URLSearchParams();
    queryParams.append('sort', 'endTime:DESC');
    queryParams.append('search', searchParams.get('search'));
    const pageSize = parseInt(searchParams.get('pageSize')) || 25;
    const page = refresh ? 1 : Math.ceil(bookings.length / pageSize) + 1;
    queryParams.append('pageSize', pageSize);
    queryParams.append('page', page);
    if (searchParams.get('filters'))
      appendQueryParamsFromSelected(searchParams.get('filters'), queryParams);

    try {
      const [response, rooms] = await Promise.all([
        getUserBookings(config.user.id, queryParams),
        getRooms() 
      ])

      if (response.data.rows) {
        const localBookings = refresh ? [] : [...bookings];
        const newBookings = response.data.rows.map((booking) => ({
          ...booking,
          "Room": rooms.data.find((room) => room.id === booking.roomId),
        }));
        setBookings([...localBookings, ...newBookings]);
        setRooms(rooms.data);
        setHasMore(localBookings.length + response.data.rows.length !== response.data.count);
      } else {
        setHasMore(false);
      }

      setCount(response.data.count); 
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }    
  }, [searchParams, bookings.length, config.user.id]);

  function appendQueryParamsFromSelected(filters, queryParams) {
    const filterObject = JSON.parse(filters);
    for (const [name, items] of Object.entries(filterObject)) {
      items.forEach(item => {
        queryParams.append(name, item);
      });
    }
  }

  function selectedListChanged(filters){
    let filteredFilters = { ...filters };
    Object.keys(filters).filter(key => filters[key].length < 1).forEach(key => {
      delete filteredFilters[key];
    })

    mapFiltersToKeys(filteredFilters);

    searchParams.set('filters', JSON.stringify(filteredFilters));
    setSearchParams(searchParams);
  }

  function mapFiltersToKeys(filteredFilters) {
    Object.keys(filteredFilters).forEach(key => {
      filteredFilters[key] = filteredFilters[key].map(item => item.key);
    });
  }

  function handleSearch(search) {
    searchParams.set('search', search);
    setSearchParams(searchParams);
  }

  function getSelectedBookingWithCost() {
    const cost = getCost(selectedBooking);
    return {
      ...selectedBooking,
      ...(cost ? { credits: getCost(selectedBooking) } : {})
    };
  }

  function getCost(booking){
    if (rooms?.length > 0 && booking?.roomId) {
      const room = rooms.find(room => room.id === booking.roomId);
      const endTime = new Date(booking.endTime);
      const startTime = new Date(booking.startTime);
      const creditsPerMin = room.credits / room.interval;
      const bookingDurationInMins = Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60)));
      return +(creditsPerMin * bookingDurationInMins).toFixed(2);
    }
  }

  function isBookingOwner(booking) {
    if (booking && booking?.Users) {
      return booking.Users.some(user => user.id === config.user.id && user.UserBooking.creator);
    }
  }

  return (
    <>
      <main className="main-container">
        <div className="main-box">
          <div className="main-header">{t.my_bookings}</div>
          <div className='bookings-filters'> 
            <SearchComponent 
              searchClass='px-4 py-2 border rounded-md mr-2 max-md:mr-4 ml-4'
              onSearchChange={handleSearch}
              clear={clear}
            >
            </SearchComponent>

            <div className="flex flex-wrap mr-2 max-md:ml-4">
              <FiltersList listFilters={filtersList} onSelectedChange={x => selectedListChanged(x)} clear={clear}/>
            </div>
            <button className='btn px-4 py-2 max-md:ml-4 w-auto max-md:mr-4' onClick={() => setClear(x => x + 1)}>
              {t.clear}
            </button>
          </div>
          <div>
            {bookings.length > 0 ? (
              <BookingsList bookingsList={bookings} onBookingClick={openOverlay} refreshBookings={() => setClear(x => x + 1)} />
            ) : (
              <div className='empty-booking md:mt-12'>
                <h1>{t.no_bookings}</h1>
                <h2>{t.want_to_create}</h2>
                <button className="btn"><Link to="/book">{t.book_a_room}</Link></button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 px-4 py-2">
            <div><h2>{t.showing.replace("{ph_length}", bookings.length).replace("{ph_count}", count)}</h2></div>
          </div>
          <div className='flex flex-row justify-center'>
            { hasMore && <button className="admin-edit-button w-[10rem] mb-2" onClick={() => fetchData()}>
              {t.show_more}
            </button> }
          </div>
        </div>
      </main>

      <Overlay
        title={(new Date(selectedBooking.endTime) > new Date()) ? t.manage_booking : 'Booking details'}
        isOpen={showOverlay} 
        onClose={closeOverlay}
        room={selectedBooking.Room}
        inputBooking={getSelectedBookingWithCost()}
        editable={(isAuthorized(ACL.BOOKING) || isBookingOwner(selectedBooking)) && (new Date(selectedBooking.endTime) > new Date())}
        newBooking={false}
      />
    </>
  );
};

export default Bookings;